import { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Autocomplete,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  IconButton,
  Switch,
} from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { useApi } from "../../hooks/useApi";
import {
  BackfillPreview,
  BillingProfileResponse,
  BillingRegion,
  UnassignedCustomer,
} from "../../types/billing";
import { BILLING_ENTITIES, billingCurrency, formatUtcDateTime, METER_NAME_LABELS } from "../../utils/billingFormat";
import { formatCurrency } from "../../utils/formatCurrency";
import BillingBackfillDialog from "./BillingBackfillDialog";
import BillingOverview from "./BillingOverview";
import BillingCustomerPane, { BillingField } from "./BillingCustomerPane";

interface Props {
  companyId: string;
  billingProfile: BillingProfileResponse;
  onChanged: () => Promise<void> | void;
  onNotify: (message: string, severity: "success" | "error") => void;
}

function formatBackfillBannerMessage(preview: BackfillPreview): string {
  const total = preview.meters.reduce((s, m) => s + m.count, 0);
  const breakdown = preview.meters
    .filter((m) => m.count > 0)
    .map((m) => `${m.count} ${METER_NAME_LABELS[m.meterName] ?? m.meterName}`)
    .join(", ");
  const noun = total === 1 ? "alert" : "alerts";
  const verb = total === 1 ? "was" : "were";
  return `This billing profile has ${total} billable ${noun} that ${verb} not billed automatically (${breakdown}). Recently-queued alerts may take a minute to clear.`;
}

export default function CompanyBillingProfilePanel({ companyId, billingProfile, onChanged, onNotify }: Props) {
  const { get, post, patch } = useApi();

  const billingProfileId = billingProfile.billingProfileId;

  const [region, setRegion] = useState<BillingRegion | null>(null);
  const [customers, setCustomers] = useState<UnassignedCustomer[] | null>(null);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [customersError, setCustomersError] = useState<string | null>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<UnassignedCustomer | null>(null);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);

  const [backfillOpen, setBackfillOpen] = useState(false);
  const [savingManualBilling, setSavingManualBilling] = useState(false);
  const [minimumFeeInput, setMinimumFeeInput] = useState("");
  const [savingMinimumFee, setSavingMinimumFee] = useState(false);
  const [minimumFeeDialogOpen, setMinimumFeeDialogOpen] = useState(false);
  const [minimumFeeError, setMinimumFeeError] = useState<string | null>(null);

  const [backfillPreview, setBackfillPreview] = useState<BackfillPreview | null>(null);

  const backfillEligible = billingProfile.assigned === true && !billingProfile.manualBilling;

  const fetchBackfillPreview = useCallback(async () => {
    try {
      const res = await get<BackfillPreview>(
        `/api/companies/${companyId}/billing-profiles/${billingProfileId}/backfill/preview?period=full`
      );
      setBackfillPreview(res);
    } catch {
      setBackfillPreview(null);
    }
  }, [get, companyId, billingProfileId]);

  // Advisory-only: fetched alongside the main billing load, never blocks
  // rendering, and self-clears if the request fails or eligibility flips off.
  useEffect(() => {
    if (!backfillEligible) {
      setBackfillPreview(null);
      return;
    }
    fetchBackfillPreview();
  }, [backfillEligible, fetchBackfillPreview]);

  const handleRegionChange = async (_: unknown, newRegion: BillingRegion | null) => {
    if (!newRegion) return;
    setRegion(newRegion);
    setSelectedCustomer(null);
    setCustomers(null);
    setCustomersError(null);
    setLoadingCustomers(true);
    try {
      const list = await get<UnassignedCustomer[]>(
        `/api/companies/${companyId}/billing/unassigned-customers?region=${newRegion}`,
        { slowApi: true }
      );
      setCustomers(list);
    } catch (err: any) {
      setCustomersError(err.message || "Failed to load customers");
    } finally {
      setLoadingCustomers(false);
    }
  };

  const handleAssign = async () => {
    if (!region || !selectedCustomer) return;
    setAssigning(true);
    setAssignError(null);
    try {
      await post(
        `/api/companies/${companyId}/billing-profiles/${billingProfileId}/assign`,
        { region, customerId: selectedCustomer.id }
      );
      setConfirmOpen(false);
      setRegion(null);
      setCustomers(null);
      setSelectedCustomer(null);
      await onChanged();
      onNotify("Billing customer assigned", "success");
    } catch (err: any) {
      setAssignError(err.message || "Failed to assign customer");
    } finally {
      setAssigning(false);
    }
  };

  const handleToggleManualBilling = async (_: unknown, checked: boolean) => {
    setSavingManualBilling(true);
    try {
      await patch(`/api/companies/${companyId}/billing-profiles/${billingProfileId}`, { manualBilling: checked });
      setRegion(null);
      setCustomers(null);
      setSelectedCustomer(null);
      await onChanged();
      onNotify(checked ? "Manual billing turned on" : "Manual billing turned off", "success");
    } catch (err: any) {
      onNotify(err.message || "Failed to update manual billing", "error");
    } finally {
      setSavingManualBilling(false);
    }
  };

  const handleSaveMinimumFee = async () => {
    const trimmed = minimumFeeInput.trim();
    const major = trimmed === "" ? null : Number(trimmed);
    if (major !== null && (!Number.isFinite(major) || major <= 0)) {
      setMinimumFeeError("Minimum fee must be a positive amount");
      return;
    }
    setMinimumFeeError(null);
    setSavingMinimumFee(true);
    try {
      await patch(`/api/companies/${companyId}/billing-profiles/${billingProfileId}`, {
        minimumMonthlyFeeCents: major === null ? null : Math.round(major * 100),
      });
      await onChanged();
      setMinimumFeeDialogOpen(false);
      onNotify("Minimum monthly fee saved", "success");
    } catch (err) {
      setMinimumFeeError(err instanceof Error ? err.message : "Failed to save minimum fee");
    } finally {
      setSavingMinimumFee(false);
    }
  };

  const openMinimumFeeDialog = () => {
    if (billingProfile.assigned !== true) return;
    setMinimumFeeInput(billingProfile.minimumMonthlyFeeCents != null ? String(billingProfile.minimumMonthlyFeeCents / 100) : "");
    setMinimumFeeError(null);
    setMinimumFeeDialogOpen(true);
  };

  const formatCustomerLabel = (c: UnassignedCustomer) => {
    const parts = [c.name, c.email].filter(Boolean);
    const head = parts.length ? parts.join(" — ") : "(no name / email)";
    return `${head}  ·  ${c.id}`;
  };

  return (
    <div>
      {billingProfile.assigned === false && (
        <Paper variant="outlined" sx={{ p: 3 }}>
          <FormControlLabel
            control={
              <Switch
                checked={billingProfile.manualBilling}
                onChange={handleToggleManualBilling}
                disabled={savingManualBilling}
              />
            }
            label="Billed manually (outside the portal)"
            sx={{ mb: 2 }}
          />

          {billingProfile.manualBilling ? (
            <Alert severity="info">
              This billing profile is billed manually outside the portal — no Stripe customer is needed.
              Turn this off to assign one.
            </Alert>
          ) : (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                No Stripe billing customer is assigned to this billing profile yet.
              </Alert>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                1. Which Chargemont entity will bill this client?
              </Typography>
              <ToggleButtonGroup
                value={region}
                exclusive
                onChange={handleRegionChange}
                sx={{
                  mb: 3,
                  "& .MuiToggleButton-root": {
                    textTransform: "none",
                    textAlign: "left",
                    alignItems: "flex-start",
                    px: 2,
                    py: 1.25,
                    minWidth: 240,
                  },
                }}
              >
                {(Object.keys(BILLING_ENTITIES) as Array<keyof typeof BILLING_ENTITIES>).map((key) => {
                  const entity = BILLING_ENTITIES[key];
                  return (
                    <ToggleButton key={key} value={key}>
                      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>{entity.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {entity.currency}
                        </Typography>
                      </Box>
                    </ToggleButton>
                  );
                })}
              </ToggleButtonGroup>

              {region && (
                <>
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    2. Choose an unassigned Stripe customer
                  </Typography>

                  {loadingCustomers && (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
                      <CircularProgress size={16} />
                      <Typography variant="body2" color="text.secondary">Loading customers…</Typography>
                    </Box>
                  )}

                  {customersError && (
                    <Alert severity="error" sx={{ mb: 2 }}>{customersError}</Alert>
                  )}

                  {customers && (
                    <>
                      <Autocomplete<UnassignedCustomer>
                        options={customers}
                        value={selectedCustomer}
                        onChange={(_, v) => setSelectedCustomer(v)}
                        getOptionLabel={formatCustomerLabel}
                        isOptionEqualToValue={(a, b) => a.id === b.id}
                        noOptionsText="No unassigned customers found"
                        renderInput={(params) => (
                          <TextField {...params} label={`Unassigned ${region} customers (${customers.length})`} size="small" />
                        )}
                        sx={{ maxWidth: 640, mb: 2 }}
                      />

                      <Button
                        variant="contained"
                        disabled={!selectedCustomer}
                        onClick={() => { setAssignError(null); setConfirmOpen(true); }}
                        sx={{ backgroundColor: "#465fff", "&:hover": { backgroundColor: "#3641f5" } }}
                      >
                        Assign
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </Paper>
      )}

      {billingProfile.assigned === true && backfillPreview && backfillPreview.meters.some((m) => m.count > 0) && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button color="inherit" size="small" onClick={() => setBackfillOpen(true)}>
              Review &amp; run backfill
            </Button>
          }
        >
          {formatBackfillBannerMessage(backfillPreview)}
        </Alert>
      )}

      {billingProfile.assigned === true && (
        <BillingOverview
          subscriptions={billingProfile.subscriptions}
          invoices={billingProfile.invoices}
          pane={
            <BillingCustomerPane
              info={billingProfile}
              details={
                <>
                  <BillingField label="Automated tracking since">{formatUtcDateTime(billingProfile.enrolledAt)}</BillingField>
                  <BillingField label="Minimum monthly fee">
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                      {billingProfile.minimumMonthlyFeeCents != null
                        ? formatCurrency(billingProfile.minimumMonthlyFeeCents, billingCurrency(billingProfile))
                        : "-"}
                      <IconButton size="small" aria-label="Edit minimum monthly fee" onClick={openMinimumFeeDialog} sx={{ p: 0.25 }}>
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </BillingField>
                </>
              }
              actions={
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<OpenInNewIcon fontSize="small" />}
                    href={`https://dashboard.stripe.com/${billingProfile.mode === "test" ? "test/" : ""}customers/${billingProfile.customer.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textTransform: "none" }}
                  >
                    Open in Stripe
                  </Button>
                  <Button size="small" variant="outlined" onClick={() => setBackfillOpen(true)} sx={{ textTransform: "none" }}>
                    Backfill unbilled alerts
                  </Button>
                </>
              }
            />
          }
        />
      )}

      <Dialog open={confirmOpen} onClose={() => !assigning && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Assign billing customer</DialogTitle>
        <DialogContent>
          {assignError && <Alert severity="error" sx={{ mb: 2 }}>{assignError}</Alert>}
          {selectedCustomer && region && (
            <Typography>
              Bill <strong>{billingProfile.billingProfileName}</strong> via <strong>{BILLING_ENTITIES[region].name}</strong>{' '}
              ({BILLING_ENTITIES[region].currency}) using Stripe customer{' '}
              <strong>{selectedCustomer.name || selectedCustomer.email || selectedCustomer.id}</strong>
              {' '}({selectedCustomer.id})?
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={assigning}>Cancel</Button>
          <Button
            onClick={handleAssign}
            variant="contained"
            disabled={assigning}
            sx={{ backgroundColor: "#465fff", "&:hover": { backgroundColor: "#3641f5" } }}
          >
            {assigning ? "Assigning…" : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>

      {billingProfile.assigned === true && (
        <BillingBackfillDialog
          open={backfillOpen}
          companyId={companyId}
          billingProfileId={billingProfileId}
          onClose={() => setBackfillOpen(false)}
          onApplied={(result) => {
            const queuedTotal = result.queued.reduce((s, m) => s + m.count, 0);
            onNotify(
              queuedTotal === 0
                ? "Nothing to backfill — no alerts in this period."
                : `Queued ${queuedTotal} alerts for billing — they'll be processed within a minute.`,
              "success",
            );
            fetchBackfillPreview();
          }}
        />
      )}

      {billingProfile.assigned === true && (
        <Dialog open={minimumFeeDialogOpen} onClose={() => !savingMinimumFee && setMinimumFeeDialogOpen(false)} maxWidth="xs" fullWidth>
          <DialogTitle>Minimum monthly fee</DialogTitle>
          <DialogContent>
            {minimumFeeError && <Alert severity="error" sx={{ mb: 2, mt: 1 }}>{minimumFeeError}</Alert>}
            <TextField
              autoFocus
              fullWidth
              size="small"
              sx={{ mt: 1 }}
              label={`Amount (${billingCurrency(billingProfile).toUpperCase()})`}
              value={minimumFeeInput}
              onChange={(e) => setMinimumFeeInput(e.target.value)}
              placeholder="No minimum"
              helperText="Leave empty to remove the minimum. Periods billed below it are reported to Slack by the daily billing audit."
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setMinimumFeeDialogOpen(false)} disabled={savingMinimumFee}>Cancel</Button>
            <Button onClick={handleSaveMinimumFee} variant="contained" disabled={savingMinimumFee}>
              {savingMinimumFee ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </Dialog>
      )}
    </div>
  );
}
